/**
 * storageQuota.ts — localStorage Quota Safety
 *
 * Provides utilities to check remaining localStorage capacity
 * and handle quota exceeded errors gracefully.
 *
 * Addresses: PRODUCT_UX_REPORT §1.5 QA "localStorage quota exceeded — No handling"
 */

import { warn, error as logError } from './logger'

/** Estimated max localStorage size (5MB for most browsers) */
const ESTIMATED_MAX_BYTES = 5 * 1024 * 1024

/**
 * Get the current total size of all localStorage data in bytes.
 */
export function getLocalStorageUsedBytes(): number {
  let total = 0
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        // Each char is 2 bytes in UTF-16
        total += (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2
      }
    }
  } catch (err) {
    logError('Failed to calculate localStorage size:', err)
  }
  return total
}

/**
 * Get estimated remaining localStorage capacity in bytes.
 */
export function getLocalStorageRemainingBytes(): number {
  return Math.max(0, ESTIMATED_MAX_BYTES - getLocalStorageUsedBytes())
}

/**
 * Get localStorage usage as a percentage (0-100).
 */
export function getLocalStorageUsagePercent(): number {
  return Math.round((getLocalStorageUsedBytes() / ESTIMATED_MAX_BYTES) * 100)
}

/**
 * Check if localStorage is nearing capacity (>80% full).
 */
export function isLocalStorageNearFull(): boolean {
  return getLocalStorageUsagePercent() > 80
}

/**
 * Safe localStorage write with quota check.
 * Returns true if write succeeded, false if quota would be exceeded.
 *
 * If the write would push usage above 90%, it logs a warning.
 * If the write fails due to QuotaExceededError, it returns false.
 */
export function safeStorageWrite(key: string, value: string): boolean {
  try {
    // Pre-check: estimate if this write would exceed quota
    const newDataSize = (key.length + value.length) * 2
    const currentUsed = getLocalStorageUsedBytes()
    const projectedUsage = currentUsed + newDataSize

    if (projectedUsage > ESTIMATED_MAX_BYTES * 0.9) {
      warn(
        `localStorage nearing capacity: ${Math.round((projectedUsage / ESTIMATED_MAX_BYTES) * 100)}% after write to "${key}" (${Math.round(newDataSize / 1024)}KB)`,
      )
    }

    localStorage.setItem(key, value)
    return true
  } catch (err: unknown) {
    if (
      err instanceof DOMException &&
      (err.name === 'QuotaExceededError' ||
        err.code === 22 ||
        err.code === 1014)
    ) {
      warn(
        `localStorage quota exceeded when writing "${key}" (${Math.round((value.length * 2) / 1024)}KB). Attempting eviction...`,
      )
      // H-4: Attempt automatic eviction and retry
      const { evictedKeys } = evictLowPriorityData()
      if (evictedKeys.length > 0) {
        try {
          localStorage.setItem(key, value)
          return true
        } catch {
          logError(
            `localStorage write failed for "${key}" even after evicting ${evictedKeys.length} keys`,
          )
          return false
        }
      }
      logError(
        `localStorage quota exceeded for "${key}" — no evictable keys found. Usage: ${getLocalStorageUsagePercent()}%`,
      )
      return false
    }
    logError(`Failed to write to localStorage ("${key}"):`, err)
    return false
  }
}

/**
 * Get a diagnostic summary of localStorage usage.
 * Useful for developer tools / diagnostics page.
 */
export function getStorageDiagnostics(): {
  usedBytes: number
  usedKB: number
  estimatedMaxKB: number
  usagePercent: number
  isNearFull: boolean
  topKeys: { key: string; sizeKB: number }[]
} {
  const usedBytes = getLocalStorageUsedBytes()
  const topKeys: { key: string; sizeKB: number }[] = []

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        const size = (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2
        topKeys.push({ key, sizeKB: Math.round(size / 1024) })
      }
    }
    topKeys.sort((a, b) => b.sizeKB - a.sizeKB)
  } catch (err) {
    logError('Failed to build storage diagnostics:', err)
  }

  return {
    usedBytes,
    usedKB: Math.round(usedBytes / 1024),
    estimatedMaxKB: Math.round(ESTIMATED_MAX_BYTES / 1024),
    usagePercent: getLocalStorageUsagePercent(),
    isNearFull: isLocalStorageNearFull(),
    topKeys: topKeys.slice(0, 10),
  }
}

/**
 * Storage key priority tiers for eviction strategy.
 * Lower number = higher priority (never evict).
 */
const EVICTION_PRIORITY: Record<string, number> = {
  // CRITICAL — never evict (tier 0)
  meester_djay_v3: 0,
  'meester-djay-learner-prefs': 0,
  'meester-djay-admin-settings': 0,
  meester_djay_governance_config_v1: 0,
  // HIGH — avoid evicting (tier 1)
  meester_djay_v3_assessment_blueprints: 1,
  meester_djay_v3_assessment_attempts: 1,
  meester_djay_v3_assessment_reports: 1,
  teacher_notes: 1,
  oefenlab_personal_records: 1,
  // MEDIUM — evict if needed (tier 2)
  meester_djay_v3_group: 2,
  meester_djay_v3_period: 2,
  meester_djay_v3_assessment_session: 2,
  // LOW — evict first (tier 3)
  fluency_intro_seen: 3,
  sprint_intro_seen: 3,
  oefenlab_free_practice_intro_seen: 3,
  oefenlab_challenge_intro_seen: 3, // canonical key — see CHALLENGE_INTRO_KEY in dataLayer.ts
  oefenlab_last_session: 3,
  oefenlab_newly_unlocked_challenge: 3,
  klascode_banner_dismissed: 3,
}

/**
 * Get the eviction priority tier for a localStorage key.
 * Unknown keys default to tier 2 (MEDIUM) to be safe.
 */
function getKeyPriority(key: string): number {
  if (EVICTION_PRIORITY[key] !== undefined) return EVICTION_PRIORITY[key]
  // AB test variant keys (ab_test_*) are low priority
  if (key.startsWith('ab_test_')) return 3
  // Unknown keys default to medium priority
  return 2
}

/**
 * Evict low-priority localStorage keys to free up space.
 *
 * Strategy: Remove keys from lowest priority tier first (tier 3),
 * then tier 2 if still needed. Never touches tier 0 (critical) or tier 1 (high).
 *
 * @param targetUsagePercent - Stop evicting when usage drops below this (default: 70%)
 * @returns Object with evicted keys and final usage percentage
 *
 * Addresses: Enterprise Audit H-4 — Storage quota LRU eviction
 */
export function evictLowPriorityData(targetUsagePercent = 70): {
  evictedKeys: string[]
  finalUsagePercent: number
  freedBytes: number
} {
  const evictedKeys: string[] = []
  let freedBytes = 0
  const startUsage = getLocalStorageUsagePercent()

  if (startUsage <= targetUsagePercent) {
    return { evictedKeys, finalUsagePercent: startUsage, freedBytes }
  }

  // Collect all keys with their sizes and priorities
  const keyEntries: { key: string; priority: number; sizeBytes: number }[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        const sizeBytes =
          (key.length + (localStorage.getItem(key)?.length ?? 0)) * 2
        keyEntries.push({ key, priority: getKeyPriority(key), sizeBytes })
      }
    }
  } catch (err) {
    logError('Failed to enumerate localStorage keys for eviction:', err)
    return {
      evictedKeys,
      finalUsagePercent: getLocalStorageUsagePercent(),
      freedBytes,
    }
  }

  // Sort by priority descending (highest tier number = evict first), then by size descending
  keyEntries.sort(
    (a, b) => b.priority - a.priority || b.sizeBytes - a.sizeBytes,
  )

  // Evict keys, stopping at tier 1 (never evict tier 0 or 1)
  for (const entry of keyEntries) {
    if (entry.priority <= 1) break // Never evict critical or high-priority keys
    if (getLocalStorageUsagePercent() <= targetUsagePercent) break

    try {
      localStorage.removeItem(entry.key)
      evictedKeys.push(entry.key)
      freedBytes += entry.sizeBytes
      warn(
        `Storage eviction: removed "${entry.key}" (tier ${entry.priority}, ${Math.round(entry.sizeBytes / 1024)}KB)`,
      )
    } catch (err) {
      logError(`Failed to evict key "${entry.key}":`, err)
    }
  }

  const finalUsage = getLocalStorageUsagePercent()
  if (evictedKeys.length > 0) {
    warn(
      `Storage eviction complete: freed ${Math.round(freedBytes / 1024)}KB, usage ${startUsage}% → ${finalUsage}%`,
    )
  }

  return { evictedKeys, finalUsagePercent: finalUsage, freedBytes }
}
